import { Tables } from "@/lib/supabase/supabase_types_override";
import { BaseConfiguration, Configuration } from "@/types/crm/configuration/configuration";
import { merge } from "lodash";


export function mergeConfigurations<T extends BaseConfiguration = Configuration>(config1?: T, config2?: T): T {

    const mergedConfig = {} as T;

    if (!config1 && !config2) {
        return mergedConfig;
    }

    mergedConfig.parameter ??= {} as T['parameter'];

    const param1 = config1?.parameter;
    const param2 = config2?.parameter;

    if (param1 || param2) {
        const mergeParameterArrays = <P extends { id: string }>(arr1?: P[], arr2?: P[]): P[] => {

            const map = new Map<string, P>();

            // Add all parameters from arr1 (default configuration)
            arr1?.forEach(param => {
                map.set(param.id, { ...param });
            });

            // Merge with arr2 (override configuration) using lodash merge
            arr2?.forEach(param => {
                const existing = map.get(param.id);

                if (existing) {
                    // Use lodash merge to deeply merge the parameters
                    // merge creates a new object, combining existing and param
                    const merged = merge({}, existing, param);
                    map.set(param.id, merged);
                } else {
                    map.set(param.id, { ...param });
                }
            });

            const result = Array.from(map.values());
            return result;
        };

        (mergedConfig.parameter as any) = {
            crm: mergeParameterArrays(param1?.crm, param2?.crm),
            survey: mergeParameterArrays(param1?.survey, param2?.survey),
            project: mergeParameterArrays(param1?.project, param2?.project),
            question: mergeParameterArrays(param1?.question, param2?.question)
        };
    }

    return mergedConfig;
}

export function getMergedConfigurationFromCrm(crm?: Tables<'crm'>): Configuration {
    if (!crm) return {};
    const defaultConfig = crm.default_configuration as Configuration;
    const config = crm.configuration as Configuration;
    return mergeConfigurations(defaultConfig, config);
}
